import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import { InvoiceFilters, EMPTY_INVOICE_FILTERS, applyInvoiceFilters } from "@/components/invoices/InvoiceFilters";
import { InvoiceTable, exportInvoicesCsv } from "@/components/invoices/InvoiceTable";
import { RecordPaymentDialog } from "@/components/invoices/RecordPaymentDialog";
import { InvoiceQuickView } from "@/components/invoices/InvoiceQuickView";
import { SendInvoiceDialog } from "@/components/invoices/SendInvoiceDialog";
import { StopRecurringDialog } from "@/components/invoices/StopRecurringDialog";
import { AutopayAdmin } from "@/components/autopay/AutopayAdmin";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [filters, setFilters] = useState(EMPTY_INVOICE_FILTERS);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const { can } = usePermissions();
  const isAdmin = can("settings.payments");
  const canRecord = can("payments.record") || can("payments.view") || can("payments.attach_check");
  const [syncing, setSyncing] = useState(false);
  const syncStripe = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/stripe/reconcile-now`);
      const n = data.recorded || 0;
      toast[n > 0 ? "success" : "info"](n > 0 ? `${n} payment${n === 1 ? "" : "s"} recorded from Stripe` : "No missed payments — everything is already recorded", { description: `${data.checked} pending checkout${data.checked === 1 ? "" : "s"} checked · ${data.expired} expired${data.errors ? ` · ${data.errors} error${data.errors === 1 ? "" : "s"}` : ""}` });
      if (n > 0) fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || "Stripe sync failed"); } finally { setSyncing(false); }
  };
  const [formData, setFormData] = useState({
    customer_id: "",
    items: [{ description: "", quantity: 1, rate: 0, amount: 0 }],
    tax: 0,
    status: "pending",
    due_date: "",
    send_via_stripe: false,
    technician_id: ""
  });

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
    axios.get(`${API}/technicians`).then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = formData.items.map((it) => ({ ...it }));
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, technician_id: formData.technician_id || null };
    try {
      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, payload);
        toast.success("Invoice updated successfully");
      } else {
        const response = await axios.post(`${API}/invoices`, payload);
        if (formData.send_via_stripe && response.data.stripe_invoice_url) {
          toast.success("Invoice created and sent via Stripe!");
          toast.info(`View invoice: ${response.data.stripe_invoice_url}`, { duration: 5000 });
        } else if (formData.send_via_stripe && !response.data.stripe_invoice_url) {
          // Invoice was created locally but Stripe sync failed
          toast.warning("Invoice created in app, but Stripe sync failed. Check Stripe configuration.");
        } else {
          toast.success("Invoice created successfully");
        }
      }
      setOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (editingInvoice ? "Failed to update invoice" : "Failed to create invoice");
      toast.error(errorMsg);
      
      // Even if there's an error (e.g., Stripe sync failed), the invoice might have been created locally
      // So refresh the list to show it
      fetchInvoices();
      setOpen(false);
      resetForm();
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      customer_id: invoice.customer_id,
      items: invoice.items.map((it) => ({ ...it })),
      tax: invoice.tax,
      status: invoice.status,
      due_date: invoice.due_date,
      technician_id: invoice.technician_id || ""
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success("Invoice deleted successfully");
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };

  const [sendInvoiceId, setSendInvoiceId] = useState(null);
  const emailInvoice = (invoiceId) => setSendInvoiceId(invoiceId);

  const textInvoice = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/sms`);
      toast.success(`Invoice ${response.data.invoice} texted to ${response.data.to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send text");
    }
  };

  const emailAndTextInvoice = (invoiceId) => setSendInvoiceId(invoiceId);

  const copyPayLink = async (invoice) => {
    let link;
    try {
      ({ data: { link } } = await axios.get(`${API}/invoices/${invoice.id}/pay-link`));
    } catch (error) {
      return toast.error(error.response?.data?.detail || "Could not create pay link");
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`View & Pay link for ${invoice.invoice_number} copied`, { description: link });
    } catch {
      toast.info(`View & Pay link for ${invoice.invoice_number}`, { description: link, duration: 15000 });
    }
  };

  const markAsRecurring = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/mark-recurring`);
      toast.success(`Invoice marked as recurring. Next reminder: ${response.data.next_billing_reminder}`);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as recurring");
    }
  };

  const [stopRecurringId, setStopRecurringId] = useState(null);
  const unmarkAsRecurring = (invoiceId) => setStopRecurringId(invoiceId);

  const resetForm = () => {
    setFormData({
      customer_id: "",
      items: [{ description: "", quantity: 1, rate: 0, amount: 0 }],
      tax: 0,
      status: "pending",
      due_date: "",
      technician_id: ""
    });
    setEditingInvoice(null);
  };

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = (checked, visible) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visible.forEach((inv) => (checked ? next.add(inv.id) : next.delete(inv.id)));
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());

  const bulkEmail = async (selected) => {
    setBulkBusy("email");
    try {
      const { data } = await axios.post(`${API}/invoices/bulk-email`, { invoice_ids: selected.map((i) => i.id) });
      if (data.failed === 0) toast.success(`Emailed ${data.sent} invoice${data.sent === 1 ? "" : "s"}`);
      else toast.warning(`Sent ${data.sent}, failed ${data.failed}: ${data.results.filter((r) => !r.success).map((r) => r.error).join("; ")}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk email failed");
    } finally {
      setBulkBusy(null);
    }
  };

  const bulkMarkPaid = async (selected) => {
    setBulkBusy("paid");
    try {
      const { data } = await axios.post(`${API}/invoices/bulk-mark-paid`, { invoice_ids: selected.map((i) => i.id) });
      toast.success(`Marked ${data.updated} invoice${data.updated === 1 ? "" : "s"} as paid`);
      clearSelection();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(null);
    }
  };

  const bulkPdf = async (selected, format) => {
    setBulkBusy(`pdf-${format}`);
    try {
      const res = await axios.post(`${API}/invoices/bulk-pdf`, { invoice_ids: selected.map((i) => i.id), format }, { responseType: "blob" });
      const match = /filename=([^;]+)/.exec(res.headers["content-disposition"] || "");
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1].trim() : `invoices.${format === "merged" ? "pdf" : "zip"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.length} invoice${selected.length === 1 ? "" : "s"} as ${format === "merged" ? "one PDF" : "zip"}`);
    } catch (error) {
      toast.error("Bulk PDF download failed");
    } finally {
      setBulkBusy(null);
    }
  };

  const exportCsv = (rows) => toast.success(`Exported ${exportInvoicesCsv(rows)} invoices to CSV`);

  const filteredInvoices = applyInvoiceFilters(
    [...invoices].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))),
    filters
  );

  return (
    <div data-testid="invoices-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Invoices</h1>
          <p className="text-slate-600">Manage and track your invoices</p>
        </div>
        <div className="flex items-center gap-2">
        {isAdmin && (
          <Button variant="outline" onClick={syncStripe} disabled={syncing} title="Check Stripe for card payments the webhook may have missed" data-testid="sync-stripe-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Stripe payments"}
          </Button>
        )}
        <Dialog open={open} onOpenChange={(isOpen) => {
          if (!isOpen || editingInvoice) resetForm();
          setOpen(isOpen);
        }}>
          <DialogTrigger asChild>
            <Button className={`bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg ${can("invoices.create") ? "" : "hidden"}`} data-testid="add-invoice-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingInvoice ? "Edit Invoice" : "Create New Invoice"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer">Customer *</Label>
                  <CustomerCombobox
                    customers={customers}
                    value={formData.customer_id}
                    onChange={(value) => setFormData({ ...formData, customer_id: value })}
                    onCreated={(c) => setCustomers((prev) => [...prev, c])}
                    allowCreate
                    testId="customer-select"
                  />
                </div>
                <div>
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Input
                    id="due_date"
                    type="date"
                    data-testid="due-date-input"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="technician">Technician (optional)</Label>
                <Select value={formData.technician_id || "none"} onValueChange={(value) => setFormData({ ...formData, technician_id: value === "none" ? "" : value })}>
                  <SelectTrigger data-testid="invoice-technician-select">
                    <SelectValue placeholder="No technician" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No technician</SelectItem>
                    {technicians.map((tech) => (
                      <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Invoice Items *</Label>
                  <Button type="button" size="sm" onClick={addItem} variant="outline">
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-2 p-3 bg-slate-50 rounded-lg">
                    <div className="col-span-5">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={item.amount}
                        readOnly
                        className="bg-slate-100"
                      />
                    </div>
                    <div className="col-span-1 flex items-center">
                      {formData.items.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(index)} className="h-10 w-10 text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tax">Tax Amount</Label>
                  <Input
                    id="tax"
                    type="number"
                    data-testid="tax-input"
                    value={formData.tax}
                    onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })} required>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-4 bg-slate-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-semibold">${formData.items.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Tax:</span>
                  <span className="font-semibold">${formData.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span className="text-blue-600">${(formData.items.reduce((sum, item) => sum + item.amount, 0) + formData.tax).toFixed(2)}</span>
                </div>
              </div>

              {!editingInvoice && (
                <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="send_via_stripe"
                    checked={formData.send_via_stripe}
                    onChange={(e) => setFormData({ ...formData, send_via_stripe: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <label htmlFor="send_via_stripe" className="font-semibold text-purple-900 cursor-pointer">
                      Send this invoice via Stripe
                    </label>
                    <p className="text-xs text-purple-700 mt-1">
                      Automatically create and email invoice to customer using Stripe
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-invoice-button">
                  {editingInvoice ? "Update" : "Create"}
                </Button>
                <Button type="button" variant="outline" onClick={() => { resetForm(); setOpen(false); }} className="flex-1" data-testid="invoice-form-cancel">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <Tabs defaultValue="invoices">
          <TabsList className="mb-4">
            <TabsTrigger value="invoices" data-testid="invoices-tab">Invoices</TabsTrigger>
            <TabsTrigger value="autopay" data-testid="autopay-tab">Autopay</TabsTrigger>
          </TabsList>
          <TabsContent value="invoices">
          <InvoiceFilters
            filters={filters}
            onChange={setFilters}
            customers={customers}
            resultCount={filteredInvoices.length}
            totalCount={invoices.length}
          />
          <InvoiceTable
            invoices={filteredInvoices}
            actions={{ downloadPDF, emailInvoice, textInvoice, emailAndTextInvoice, onEdit: handleEdit, onDelete: handleDelete, markAsRecurring, unmarkAsRecurring: can("invoices.recurring_manage") ? unmarkAsRecurring : null, copyPayLink, onView: setViewInvoice, onRecordPayment: canRecord ? setPaymentInvoice : null }}
            selectedIds={selectedIds}
            onToggle={toggleSelected}
            onToggleAll={toggleAll}
            onClearSelection={clearSelection}
            bulk={{ email: bulkEmail, markPaid: bulkMarkPaid, pdf: bulkPdf, exportCsv }}
            bulkBusy={bulkBusy}
          />
          {paymentInvoice && <RecordPaymentDialog invoice={paymentInvoice} onClose={() => setPaymentInvoice(null)} onChanged={fetchInvoices} />}
          {sendInvoiceId && <SendInvoiceDialog invoiceId={sendInvoiceId} onClose={() => setSendInvoiceId(null)} onSent={fetchInvoices} />}
          {stopRecurringId && <StopRecurringDialog invoiceId={stopRecurringId} onClose={() => setStopRecurringId(null)} onDone={fetchInvoices} />}
          {viewInvoice && <InvoiceQuickView invoice={invoices.find((i) => i.id === viewInvoice.id) || viewInvoice} onClose={() => setViewInvoice(null)} actions={{ downloadPDF, emailInvoice, textInvoice, emailAndTextInvoice, onEdit: handleEdit, copyPayLink, onRecordPayment: canRecord ? setPaymentInvoice : null }} />}
          </TabsContent>
          <TabsContent value="autopay"><AutopayAdmin onChanged={fetchInvoices} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
